import React from "react";
import { Tag, Button, Space } from "antd";
import { useTranslation } from "react-i18next";
import Datatable from "components/Common/dataTable";
import { Payroll } from "../../../types/payrollTypes";
import { formatMoney } from "helpers/numberHelper";
import { formatToLocaleDate } from "utils/dateUtils";
import ActionsDropdown, { Action } from "components/Common/ActionsDropdown";
import PayrollStatusBadge from "components/Common/Badge/PayrollStatusBadge";
import { all_routes } from "router/all_routes";
import UserProfileHeader from "components/Common/ui/UserProfileHeader";
import { useAbility } from "config/abilityContext";

type Props = {
  dataSource: Payroll[];
  loading?: boolean;
  onUpdateStatus: (payrollId: string, status: string) => void;
  dataPaginate: any;
  onChange: (pg: any) => void;
};

const PayrollTable: React.FC<Props> = ({ dataSource, loading, onUpdateStatus, dataPaginate, onChange }) => {
  const { t } = useTranslation();
  const ability = useAbility();

  const columns = [
    {
      title: t("payroll.form.staff"),
      key: "staff",
      render: (v: string, r: Payroll) =>
        r.staff ? <UserProfileHeader firstName={r.staff.user?.firstName ?? ""} lastName={r.staff.user?.lastName ?? ""} photo={r.staff.user?.photo ?? ""}  subtitle={r.staff.user?.email ?? ""} /> : '-',
    },
    {
      title: t("payroll.form.period"),
      key: "period",
      align: "center" as const,
      render: (v: string, r: Payroll) => `${formatToLocaleDate(r.startDate)} - ${formatToLocaleDate(r.endDate)}`,
    },
    {
      title: t("payroll.form.gross_amount"),
      dataIndex: "grossAmount",
      key: "grossAmount",
      align: "center" as const,
      render: (amount: number) => formatMoney(amount),
    },
    {
      title: t("payroll.form.net_amount"),
      dataIndex: "netAmount",
      key: "netAmount",
      align: "center" as const,
      render: (amount: number) => formatMoney(amount),
    },
    {
      title: t("payroll.form.status"),
      dataIndex: "computedStatus",
      key: "computedStatus",
      align: "center" as const,
      render: (computedStatus: string) => (
        <PayrollStatusBadge status={computedStatus as any} />
      ),
    },
    {
      title: t("actions"),
      key: "actions",
      align: "center" as const,
      render: (v: string, r: Payroll) => {
        const actions: Action[] = [
          {
            label: t("payroll.form.view_details"),
            icon: "ti ti-eye",
            actionType: "link",
            to: all_routes.detailPayroll.replace(":idPayroll", r.id),
          },
          {
            label: t("payroll.form.mark_as_paid"),
            icon: "ti ti-check",
            actionType: "link",
            hidden: r.status === "paid" || !ability.can("update", "Payroll"),
            onClick: () => onUpdateStatus(r.id, 'paid'),
          },
          {
            label: t("payroll.form.mark_as_failed"),
            icon: "ti ti-x",
            actionType: "link",
            hidden: r.status === "failed" || !ability.can("update", "Payroll"),
            onClick: () => onUpdateStatus(r.id, 'failed'),
          },
        ];
        return <ActionsDropdown actions={actions} />;
      },
    },
  ];

  return (
    <div className="table-responsive">
      <Datatable
        rowKey="id"
        columns={columns}
        dataSource={dataSource ?? []}
        loading={loading}
        pagination={true}
        dataPaginate={dataPaginate}
        onChange={onChange}
      />
    </div>
  );
};

export default PayrollTable;